import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useState, useEffect, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, Select, Typography, Space } from 'antd';
import {
  InboxOutlined,
  UndoOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  GlobalOutlined,
  SwapOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import {
  useAuthStore,
  useChannelStore,
  useMessageStore,
  useServerStore,
  useThreadStore,
} from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { captureChannelContext } from '../stores/useChannelStore';
import { updateChannel, deleteChannel, archiveChannel, moveChannel } from '../lib/api';
import { useCan } from '../hooks/usePermissions';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import { channelDisplayName } from '../lib/channelDisplay';
import { deriveProjectAuthority } from '../lib/projectAuthority';
import { routes } from '../lib/routes';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { useToast } from './Toast';
import zoneStyles from './ZoneManagementDialog.module.css';

interface Props {
  channelId: string;
  onClose: () => void;
}

export default function ChannelSettingsModal({ channelId, onClose }: Props) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.currentUser);
  const channels = useChannelStore((s) => s.channels);
  const zones = useChannelStore((s) => s.zones);
  const loadChannels = useChannelStore((s) => s.loadChannels);
  const removeChannel = useChannelStore((s) => s.removeChannel);
  const guildId = useAuthStore((s) => s.guildId);
  const guilds = useServerStore((s) => s.guilds);
  const guildSnapshotsAuthoritative = useServerStore(
    (state) => state.guildsLoaded && state.guildsAuthorityRevision === state.authorityRevision,
  );
  const sourceAuthority = useProjectAuthority(guildId);
  const { showToast } = useToast();
  const channel = channels.find((c) => c.id === channelId);

  const channelName = channel?.name ?? '';
  const isGeneral = channelName === 'general';
  const isChannelOwner = currentUser?.id === channel?.created_by;
  const canDeleteChannel = useCan('channel.delete', channelId);
  const canManageVisibility = useCan('channel.manage_visibility', channelId);
  const canDelete = isChannelOwner && canDeleteChannel && !isGeneral;
  const canArchive = isChannelOwner && canManageVisibility && !isGeneral;
  const isTextChannel = channel?.channel_type === 0;
  const zone =
    isTextChannel && channel.parent_id !== null
      ? zones.find((entry) => entry.id === channel.parent_id)
      : undefined;
  const hasNamedPlacement = isTextChannel && channel.parent_id !== null;
  const isMoveableRoot =
    isTextChannel && !hasNamedPlacement && !isGeneral && channelName !== 'welcome';
  const managesSourceGuild = sourceAuthority.ready && sourceAuthority.canManageProject;
  const moveTargets = guilds.filter(
    (guild) =>
      guild.id !== guildId &&
      deriveProjectAuthority(
        currentUser?.id ?? null,
        guild,
        undefined,
        guildSnapshotsAuthoritative,
        false,
      ).canManageProject,
  );
  const canMove = isChannelOwner && isMoveableRoot && managesSourceGuild && moveTargets.length > 0;

  const [name, setName] = useState(channelName);
  const [description, setDescription] = useState(channel?.topic ?? '');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmingMove, setConfirmingMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string | undefined>();
  const [moveConfirmation, setMoveConfirmation] = useState('');
  const [moving, setMoving] = useState(false);

  const isArchived = (channel?.archived_at ?? null) != null;
  const visibility = channel?.visibility ?? 'public';

  const handleVisibilitySwitch = async () => {
    const target = visibility === 'public' ? 'private' : 'public';
    setSwitching(true);
    try {
      await updateChannel(channelId, { visibility: target });
      await loadChannels();
      showToast(target === 'private' ? msg`Channel is now private.` : msg`Channel is now public.`);
    } catch (err) {
      showToast(errorMessage(err, msg`Failed to switch`));
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    setName(channelName);
    setDescription(channel?.topic ?? '');
  }, [channelId, channelName, channel?.topic]);

  useEffect(() => {
    if (
      confirmingMove &&
      (!canMove || !moveTarget || !moveTargets.some((target) => target.id === moveTarget))
    ) {
      setConfirmingMove(false);
      setMoveTarget(undefined);
      setMoveConfirmation('');
    }
  }, [canMove, confirmingMove, moveTarget, moveTargets]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateChannel(channelId, { name, topic: description });
      await loadChannels();
      showToast(msg`Channel updated`);
      onClose();
    } catch (err) {
      showToast(errorMessage(err, msg`Failed to save`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChannel(channelId);
      removeChannel(channelId);
      onClose();
      showToast(msg`Channel deleted`);
    } catch (err) {
      showToast(errorMessage(err, msg`Delete failed`));
      setDeleting(false);
    }
  };

  const openMoveConfirmation = () => {
    setMoveTarget(moveTargets[0]?.id);
    setMoveConfirmation('');
    setConfirmingMove(true);
  };

  const hasLiveMoveAuthority = (actorId: string, sourceGuildId: string, targetGuildId: string) => {
    const servers = useServerStore.getState();
    if (servers.accountId !== actorId) return false;
    const guildSnapshotReady =
      servers.guildsLoaded && servers.guildsAuthorityRevision === servers.authorityRevision;
    const sourceGuild = servers.guilds.find((guild) => guild.id === sourceGuildId);
    const sourceViewer =
      servers.loadedGuildId === sourceGuildId
        ? servers.members.find((member) => member.user_id === actorId)
        : undefined;
    const sourceAuthorityNow = deriveProjectAuthority(
      actorId,
      sourceGuild,
      sourceViewer,
      guildSnapshotReady &&
        servers.loaded &&
        servers.loadedGuildId === sourceGuildId &&
        servers.membersAuthorityRevision === servers.authorityRevision,
      true,
    );
    const targetAuthorityNow = deriveProjectAuthority(
      actorId,
      servers.guilds.find((guild) => guild.id === targetGuildId),
      undefined,
      guildSnapshotReady,
      false,
    );
    return sourceAuthorityNow.canManageProject && targetAuthorityNow.canManageProject;
  };

  const handleMove = async () => {
    if (
      !canMove ||
      !channel ||
      !guildId ||
      !moveTarget ||
      !moveTargets.some((target) => target.id === moveTarget) ||
      moveConfirmation !== channel.name
    )
      return;
    const sourceGuildId = guildId;
    const actorId = currentUser?.id;
    const initialAuth = useAuthStore.getState();
    if (
      !actorId ||
      initialAuth.currentUser?.id !== actorId ||
      initialAuth.guildId !== sourceGuildId ||
      !hasLiveMoveAuthority(actorId, sourceGuildId, moveTarget)
    )
      return;
    const sameContext = captureChannelContext();
    setMoving(true);
    try {
      const moved = await moveChannel(sourceGuildId, channel.id, moveTarget);
      if (!sameContext() || !hasLiveMoveAuthority(actorId, sourceGuildId, moveTarget)) return;
      const subtree = moved.source_subtree_channel_ids;
      // Commit the authoritative destination before removing source rows. A
      // deferred route update would let the shell redirect the missing source
      // channel to its fallback while this navigation is still queued.
      navigate(routes.channel(moved.target_guild_id, moved.channel_id), { flushSync: true });
      if (sameContext()) {
        useChannelStore.getState().purgeSubtree(subtree);
        useMessageStore.getState().purgeChannels(subtree);
        useThreadStore.getState().purgeChannels(subtree);
        useTaskStore.getState().purgeChannels(subtree);
      }
      setConfirmingMove(false);
      onClose();
      showToast(msg`Channel moved.`);
    } catch (err) {
      if (sameContext()) showToast(errorMessage(err, msg`Failed to move channel`));
    } finally {
      setMoving(false);
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setDescription(event.target.value);

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await archiveChannel(channelId, !isArchived);
      await loadChannels();
      showToast(isArchived ? msg`Channel restored` : msg`Channel archived`);
    } catch (err) {
      showToast(errorMessage(err, msg`Archive failed`));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
      <Modal open title={t`EDIT CHANNEL`} onCancel={onClose} footer={null} width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          <div>
            <Typography.Text
              strong
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              <Trans>Name</Trans>
              <span style={{ color: 'var(--danger, #ff4d4f)' }}>*</span>
            </Typography.Text>
            <Input
              value={name}
              onChange={handleNameChange}
              disabled={isGeneral}
              style={{ marginTop: 4 }}
            />
            {isGeneral && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <Trans>The #general channel cannot be renamed</Trans>
              </Typography.Text>
            )}
          </div>

          <div>
            <Typography.Text
              strong
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              <Trans>
                Description <span style={{ color: '#999' }}>(optional)</span>
              </Trans>
            </Typography.Text>
            <Input.TextArea
              value={description}
              onChange={handleDescriptionChange}
              rows={3}
              style={{ marginTop: 4 }}
            />
          </div>

          {isTextChannel && (
            <div className={zoneStyles.field}>
              <Typography.Text strong>
                <Trans>Zone</Trans>
              </Typography.Text>
              <div
                className={zoneStyles.placement}
                aria-label={t`Channel Zone`}
                data-testid="channel-zone-placement"
              >
                {hasNamedPlacement && <AppstoreOutlined aria-hidden />}
                <span>
                  {hasNamedPlacement
                    ? zone
                      ? t`Zone: ${zone.name}`
                      : t`Zone unavailable`
                    : t`Default (no Zone)`}
                </span>
                <LockOutlined aria-label={t`Read-only placement`} style={{ marginLeft: 'auto' }} />
              </div>
              <p className={zoneStyles.hint}>
                <Trans>
                  Assigned at creation. Placement is read-only and does not affect channel access.
                </Trans>
              </p>
            </div>
          )}

          {canManageVisibility && !isGeneral && (
            <div>
              <Typography.Text
                strong
                style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                <Trans>Visibility</Trans>
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <Typography.Text>
                  {visibility === 'public' ? (
                    <>
                      <GlobalOutlined aria-hidden="true" /> <Trans>Public</Trans>
                    </>
                  ) : (
                    <>
                      <LockOutlined aria-hidden="true" /> <Trans>Private</Trans>
                    </>
                  )}
                </Typography.Text>
                <Button size="small" onClick={handleVisibilitySwitch} loading={switching}>
                  {visibility === 'public' ? t`Switch to Private` : t`Switch to Public`}
                </Button>
              </div>
            </div>
          )}

          <Space>
            <Button onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="primary" onClick={handleSave} loading={saving}>
              <Trans>Save Changes</Trans>
            </Button>
          </Space>

          {hasNamedPlacement && isChannelOwner && managesSourceGuild && (
            <div className={zoneStyles.field}>
              <Button block disabled icon={<SwapOutlined aria-hidden="true" />}>
                <Trans>Move to another Project</Trans>
              </Button>
              <p className={zoneStyles.hint}>
                <Trans>
                  Channels in a named Zone stay in their original Project. Placement cannot be
                  changed.
                </Trans>
              </p>
            </div>
          )}

          {(canArchive || canDelete || canMove) && (
            <>
              <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }} />
              <Space orientation="vertical" style={{ width: '100%' }}>
                {canArchive && (
                  <Button
                    block
                    icon={
                      isArchived ? (
                        <UndoOutlined aria-hidden="true" />
                      ) : (
                        <EyeInvisibleOutlined aria-hidden="true" />
                      )
                    }
                    onClick={handleArchive}
                    loading={archiving}
                    style={{ background: '#f5deb3', borderColor: '#f5deb3' }}
                  >
                    {isArchived
                      ? t`Restore #${channelDisplayName(channel!)}`
                      : t`Hide #${channelDisplayName(channel!)}`}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    block
                    danger
                    icon={<DeleteOutlined aria-hidden="true" />}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    <Trans>Delete #{channelDisplayName(channel!)}</Trans>
                  </Button>
                )}
                {canMove && (
                  <Button
                    danger
                    block
                    icon={<SwapOutlined aria-hidden="true" />}
                    onClick={openMoveConfirmation}
                    data-action="move-channel"
                  >
                    <Trans>Move #{channelDisplayName(channel)} to another Project</Trans>
                  </Button>
                )}
              </Space>
            </>
          )}
        </div>
      </Modal>
      {confirmingDelete && (
        <ConfirmDeleteModal
          channelName={channelDisplayName(channel!)}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
          loading={deleting}
        />
      )}
      <Modal
        open={confirmingMove}
        destroyOnHidden
        title={t`Move #${channelDisplayName(channel!)}`}
        onCancel={() => !moving && setConfirmingMove(false)}
        cancelButtonProps={{ disabled: moving }}
        okText={t`Move channel`}
        okButtonProps={{
          danger: true,
          loading: moving,
          disabled: !moveTarget || moveConfirmation !== channelName,
        }}
        onOk={() => void handleMove()}
      >
        <Typography.Paragraph>
          <Trans>
            This is a transfer, not a copy. The channel, all threads, messages, tasks, files,
            authorship, assignments, and activity history keep their IDs and remain complete.
          </Trans>
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          <Trans>
            Members, invitations, layouts, and other access derived from the current Project are
            removed. Only your new owner access is created in the target Project.
          </Trans>
        </Typography.Paragraph>
        <Typography.Text strong>
          <Trans>Target Project</Trans>
        </Typography.Text>
        <Select
          aria-label={t`Target Project`}
          style={{ width: '100%', marginTop: 6, marginBottom: 16 }}
          value={moveTarget}
          onChange={setMoveTarget}
          disabled={moving}
          options={moveTargets.map((guild) => ({ value: guild.id, label: guild.name }))}
        />
        <Typography.Text strong>
          <Trans>Type {channelName} to confirm</Trans>
        </Typography.Text>
        <Input
          aria-label={t`Channel name confirmation`}
          style={{ marginTop: 6 }}
          value={moveConfirmation}
          onChange={(event) => setMoveConfirmation(event.target.value)}
          disabled={moving}
        />
      </Modal>
    </>
  );
}
